import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { GatedRoute } from '../components/GatedRoute.tsx';
import { Panel } from '../components/primitives.tsx';
import { LoginAuditLog } from './audit/LoginAuditLog.tsx';

/**
 * Several gated destinations, and the point is the difference between them.
 *
 * `GatedRoute` itself, and its two refusal panels, live in `components/GatedRoute.tsx` now —
 * `routes/patients/PatientListPage.tsx` and `PatientChartPage.tsx` are a second and third caller of
 * the identical shape, so it moved out from under this file rather than being copied a second time.
 * See that file's own header for the capability-vs-entitlement reasoning in full.
 *
 * `AnalyticsPage`/`DermatologyPage`/`AestheticsPage`/`OrthopedicsPage` below have no content yet, and
 * each says so
 * plainly rather than showing invented data. No synthetic-realistic PHI appears anywhere in this
 * workspace (`docs/roadmap.md` §7, invariant 3). `AuditPage` is the one exception, as of 2026-08-18
 * (direct instruction: "we need a login / logout log") — its content is real, live data from
 * `GET /api/audit/login-events`, not a stub; see `audit/LoginAuditLog.tsx`'s own header.
 */

export function AnalyticsPage() {
  const { t } = useTranslation('gatedPages');
  return (
    <GatedRoute title={t('analytics.title')} capability={null} feature="analytics">
      <Panel>
        <p className="m-0 text-sm text-ink">{t('analytics.body')}</p>
      </Panel>
    </GatedRoute>
  );
}

export function DermatologyPage() {
  const { t } = useTranslation('gatedPages');
  return (
    <GatedRoute title={t('dermatology.title')} capability="chart:read" feature="dermatologyModule">
      <Panel>
        <p className="m-0 text-sm text-ink">
          {t('dermatology.bodyIntro')}{' '}
          <Link to="/patients" className="font-medium text-accent-text hover:underline">
            {t('dermatology.findPatientLink')}
          </Link>{' '}
          {t('dermatology.bodyOutro')}
        </p>
      </Panel>
    </GatedRoute>
  );
}

export function PhysicalTherapyPage() {
  const { t } = useTranslation('gatedPages');
  return (
    <GatedRoute
      title={t('physicalTherapy.title')}
      capability="chart:read"
      feature="physicalTherapyModule"
    >
      <Panel>
        <p className="m-0 text-sm text-ink">
          {t('physicalTherapy.bodyIntro')}{' '}
          <Link to="/patients" className="font-medium text-accent-text hover:underline">
            {t('physicalTherapy.findPatientLink')}
          </Link>{' '}
          {t('physicalTherapy.bodyOutro')}
        </p>
      </Panel>
    </GatedRoute>
  );
}

export function PediatricsPage() {
  const { t } = useTranslation('gatedPages');
  return (
    <GatedRoute title={t('pediatrics.title')} capability="chart:read" feature="pediatricsModule">
      <Panel>
        <p className="m-0 text-sm text-ink">
          {t('pediatrics.bodyIntro')}{' '}
          <Link to="/patients" className="font-medium text-accent-text hover:underline">
            {t('pediatrics.findPatientLink')}
          </Link>{' '}
          {t('pediatrics.bodyOutro')}
        </p>
      </Panel>
    </GatedRoute>
  );
}

export function AestheticsPage() {
  const { t } = useTranslation('gatedPages');
  return (
    <GatedRoute title={t('aesthetics.title')} capability="chart:read" feature="aestheticsModule">
      <Panel>
        <p className="m-0 text-sm text-ink">
          {t('aesthetics.bodyIntro')}{' '}
          <Link to="/patients" className="font-medium text-accent-text hover:underline">
            {t('aesthetics.findPatientLink')}
          </Link>{' '}
          {t('aesthetics.bodyOutro')}
        </p>
      </Panel>
    </GatedRoute>
  );
}

export function GastroenterologyPage() {
  const { t } = useTranslation('gatedPages');
  return (
    <GatedRoute
      title={t('gastroenterology.title')}
      capability="chart:read"
      feature="gastroenterologyModule"
    >
      <Panel>
        <p className="m-0 text-sm text-ink">
          {t('gastroenterology.bodyIntro')}{' '}
          <Link to="/patients" className="font-medium text-accent-text hover:underline">
            {t('gastroenterology.findPatientLink')}
          </Link>{' '}
          {t('gastroenterology.bodyOutro')}
        </p>
      </Panel>
    </GatedRoute>
  );
}

export function OrthopedicsPage() {
  const { t } = useTranslation('gatedPages');
  return (
    <GatedRoute title={t('orthopedics.title')} capability="chart:read" feature="orthopedicsModule">
      <Panel>
        <p className="m-0 text-sm text-ink">
          {t('orthopedics.bodyIntro')}{' '}
          <Link to="/patients" className="font-medium text-accent-text hover:underline">
            {t('orthopedics.findPatientLink')}
          </Link>{' '}
          {t('orthopedics.bodyOutro')}
        </p>
      </Panel>
    </GatedRoute>
  );
}

/**
 * Unlike the sibling stubs above, this one is honest about a real, deliberate gap rather than
 * pointing at a per-patient tab that doesn't exist yet: `ophthalmologyModule`'s server side (M10) is
 * fully built — refractions, eyewear-Rx release, imaging reports, cataract surgical plans, all
 * audited and RLS-scoped — but no `PatientOphthalmologyPage` or chart tab consumes it yet. Flagged
 * plainly here, and in `BACKLOG.md` M10's own row, rather than shipping a `find a patient` link that
 * leads nowhere real (`SpecialtyModulesPage.tsx`'s own header records the 2026-08-18 incident this
 * would otherwise repeat).
 */
export function OphthalmologyPage() {
  const { t } = useTranslation('gatedPages');
  return (
    <GatedRoute
      title={t('ophthalmology.title')}
      capability="chart:read"
      feature="ophthalmologyModule"
    >
      <Panel>
        <p className="m-0 text-sm text-ink">{t('ophthalmology.body')}</p>
      </Panel>
    </GatedRoute>
  );
}

export function AuditPage() {
  const { t } = useTranslation('gatedPages');
  return (
    <GatedRoute
      title={t('audit.title')}
      capability="audit:read"
      feature={null}
      helpTopicId="audit-log"
    >
      <LoginAuditLog />
    </GatedRoute>
  );
}
